import { store } from '../app/store';
import type { StationConfig, StationKey } from '../constants/stationConfig';
import { getApiGatewayUrl } from './deviceStatusHandler';
import apigClientFactory, { type ApiGatewayClient, type ApiGatewayClientResponse } from 'aws-api-gateway-client';

export interface ResolveStationResponse extends StationConfig {
    readonly site: string;
    readonly stationNumber: string;
}

export interface ListDrishtiSitesResponse {
    readonly sites: readonly string[];
}

export const isListDrishtiSitesResponse = (result: unknown): result is ListDrishtiSitesResponse => {
    const sites = (result as { sites?: unknown } | null)?.sites;
    return Array.isArray(sites) && sites.every((site) => typeof site === 'string');
};

export interface ListDrishtiStationsResponse {
    readonly stationNumbers: readonly string[];
}

export const isListDrishtiStationsResponse = (result: unknown): result is ListDrishtiStationsResponse => {
    const stationNumbers = (result as { stationNumbers?: unknown } | null)?.stationNumbers;
    return Array.isArray(stationNumbers) && stationNumbers.every((number) => typeof number === 'string');
};

export interface StartDrishtiInstallRequest extends StationKey {
    readonly deviceSerial: string;
}

export const DRISHTI_INSTALL_OUTCOME = {
    STARTED: 'STARTED',
    RESUMED: 'RESUMED',
    BLOCKED_DEVICE_ACTIVE_ELSEWHERE: 'BLOCKED_DEVICE_ACTIVE_ELSEWHERE',
    BLOCKED_STATION_OCCUPIED: 'BLOCKED_STATION_OCCUPIED',
} as const;
export type DrishtiInstallOutcome = (typeof DRISHTI_INSTALL_OUTCOME)[keyof typeof DRISHTI_INSTALL_OUTCOME];

const DRISHTI_INSTALL_OUTCOMES: readonly string[] = Object.values(DRISHTI_INSTALL_OUTCOME);

export interface StartDrishtiInstallResponse {
    readonly outcome: DrishtiInstallOutcome;
    readonly deviceId: string;
    readonly lifecycleState: string | null;
    readonly blockingDeviceId: string | null;
    readonly assignedSite: string | null;
    readonly assignedStationNumber: string | null;
}

const optionalString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

export const toStartDrishtiInstallResponse = (body: unknown): StartDrishtiInstallResponse | null => {
    if (typeof body !== 'object' || body === null) {
        return null;
    }
    const { outcome, deviceId, lifecycleState, blockingDeviceId, assignedSite, assignedStationNumber } = body as Record<
        string,
        unknown
    >;
    if (typeof deviceId !== 'string' || typeof outcome !== 'string' || !DRISHTI_INSTALL_OUTCOMES.includes(outcome)) {
        return null;
    }
    return {
        outcome: outcome as DrishtiInstallOutcome,
        deviceId,
        lifecycleState: optionalString(lifecycleState),
        blockingDeviceId: optionalString(blockingDeviceId),
        assignedSite: optionalString(assignedSite),
        assignedStationNumber: optionalString(assignedStationNumber),
    };
};

export interface EndDrishtiInstallRequest {
    readonly deviceId: string;
}

export interface EndDrishtiInstallResponse {
    readonly deviceId: string;
}

export const isEndDrishtiInstallResponse = (result: unknown): result is EndDrishtiInstallResponse =>
    typeof (result as EndDrishtiInstallResponse | null)?.deviceId === 'string';

export interface GetDrishtiInstallGuideResponse {
    readonly url: string;
}

export const isGetDrishtiInstallGuideResponse = (result: unknown): result is GetDrishtiInstallGuideResponse =>
    typeof (result as GetDrishtiInstallGuideResponse | null)?.url === 'string';

// ---------------------------------------------------------------------------
// Calibration types
// ---------------------------------------------------------------------------

/**
 * Workflow-level calibration status (top-level `status` field on the poll response).
 *
 * These three values are the frontend ↔ installer-backend contract, matching the finalized
 * calibration query response. The upstream calibration service has richer step-function
 * states (SUBMITTED / CAPTURING / COMPUTING / SYNCING / etc.); the installer-backend
 * collapses those to this smaller enum at its seam so the frontend only reacts to what
 * actually changes what we render.
 *
 * - IN_PROGRESS: keep polling and show the progress screen
 * - SUCCEEDED:   terminal, station is live
 * - FAILED:      terminal. The nested `failure` object carries the reason code and the
 *                remediation the installer should follow before retrying. Retry is gated by the
 *                frontend's attempt counter (RETRY_CAP), not the failure payload.
 */
export const CALIBRATION_STATUS = {
    IN_PROGRESS: 'IN_PROGRESS',
    SUCCEEDED: 'SUCCEEDED',
    FAILED: 'FAILED',
} as const;
export type CalibrationStatus = (typeof CALIBRATION_STATUS)[keyof typeof CALIBRATION_STATUS];

export const TERMINAL_CALIBRATION_STATUSES: ReadonlySet<CalibrationStatus> = new Set<CalibrationStatus>([
    CALIBRATION_STATUS.SUCCEEDED,
    CALIBRATION_STATUS.FAILED,
]);

export const isTerminalCalibrationStatus = (status: CalibrationStatus): boolean =>
    TERMINAL_CALIBRATION_STATUSES.has(status);

/**
 * Total number of calibration attempts allowed per device on a single install. The installer
 * backend does not count attempts — the frontend enforces the cap. Once
 * `calibration.attemptCount` reaches this value, the failure sub-screen shows the escalation
 * copy instead of a Retry button.
 */
export const RETRY_CAP = 50;

/**
 * One remediation instruction from the calibration service, rendered verbatim on the
 * failure screen. `instruction` is the only field the frontend consumes.
 */
export interface Remediation {
    readonly instruction: string;
    readonly [extra: string]: unknown;
}

/**
 * Structured failure payload nested inside `CalibrationStatusResponse` when `status === FAILED`.
 *
 * - `code`: machine-readable reason (e.g. 'CALIBRATION_FAILED', 'SYSTEM_DEVICE_TIMEOUT').
 * - `message`: human-readable summary. Used for logging; the installer-facing copy comes from
 *              `remediation` instead.
 * - `calibrationRetryable`: per-failure hint from the backend on whether the failure is
 *          worth an immediate retry. The frontend does not gate Retry on this flag in v0
 *          (both known failure codes come back as `false` since the fix is physical, and
 *          the installer is expected to remediate before retrying); it is stored for logging
 *          and future use.
 * - `remediation`: always an array (may be empty). Each entry has an `instruction` string
 *          rendered as a bullet on the failure screen.
 */
export interface CalibrationFailure {
    readonly code: string;
    readonly message: string;
    readonly calibrationRetryable: boolean;
    readonly remediation: Remediation[];
}

// site and stationNumber are the pair the install is keyed on: the site code, and the 4-digit
// number the technician entered (the science result payload calls that number station_id).
export interface StartCalibrationRequest {
    readonly deviceId: string;
    readonly site: string;
    readonly stationNumber: string;
    readonly dryRun?: boolean;
}

export interface StartCalibrationResponse {
    readonly deviceId: string;
    readonly calibrationWorkflow: string;
    readonly status: CalibrationStatus;
}

export interface CalibrationStatusResponse {
    readonly calibrationId: string;
    readonly deviceId: string;
    readonly status: CalibrationStatus;
    readonly createdAt: string;
    readonly updatedAt: string;
    // Present only when status === FAILED. Carries the reason code and remediation.
    readonly failure?: CalibrationFailure;
}

export interface CompleteCalibrationResponse {
    readonly escalated: boolean;
    readonly failedAttemptCount: number;
}

export interface ErrorResponse {
    readonly message?: string;
    readonly status?: number;
}

interface ApiErrorLike {
    readonly message?: string;
    readonly response?: {
        readonly data?: unknown;
        readonly status?: number;
    };
}

const parseBody = <T>(data: unknown): T => (typeof data === 'string' ? JSON.parse(data) : data) as T;

const toErrorResponse = (rejection: unknown): ErrorResponse => {
    const error: ApiErrorLike = typeof rejection === 'object' && rejection !== null ? (rejection as ApiErrorLike) : {};
    const rawBody = error.response?.data;
    let parsedBody: { message?: string; statusCode?: number } | undefined;
    if (rawBody !== undefined && rawBody !== null) {
        try {
            parsedBody = parseBody(rawBody);
        } catch {
            // Malformed error body — fall back to the transport-level message/status below.
            parsedBody = undefined;
        }
    }
    return {
        message: parsedBody?.message ?? error.message,
        status: parsedBody?.statusCode ?? error.response?.status,
    };
};

const dispatchResponse = <T>(
    request: Promise<ApiGatewayClientResponse>,
    setResult: (success: boolean, result: T | ErrorResponse) => void,
) => {
    request
        .then((response) => {
            setResult(true, parseBody<T>(response.data));
        })
        .catch((rejection: unknown) => {
            setResult(false, toErrorResponse(rejection));
        });
};

const newApiClient = (): ApiGatewayClient => apigClientFactory.newClient({ invokeUrl: getApiGatewayUrl(), retries: 3 });

// Named rather than "whichever session is signed in", so a FleetEdge token cannot reach these.
const drishtiJwt = (): string | undefined => store.getState().drishtiUserData.session?.jwt;

export const listDrishtiSites = (
    setResult: (success: boolean, result: ListDrishtiSitesResponse | ErrorResponse) => void,
) => {
    dispatchResponse<ListDrishtiSitesResponse>(
        newApiClient().invokeApi({}, '/list_drishti_sites', 'GET', { headers: { Authorization: drishtiJwt() } }, null),
        setResult,
    );
};

export const listDrishtiStations = (
    site: string,
    setResult: (success: boolean, result: ListDrishtiStationsResponse | ErrorResponse) => void,
) => {
    dispatchResponse<ListDrishtiStationsResponse>(
        newApiClient().invokeApi(
            {},
            '/list_drishti_stations',
            'GET',
            {
                queryParams: { site },
                headers: { Authorization: drishtiJwt() },
            },
            null,
        ),
        setResult,
    );
};

export const resolveStation = (
    station: StationKey,
    setResult: (success: boolean, result: ResolveStationResponse | ErrorResponse) => void,
) => {
    dispatchResponse<ResolveStationResponse>(
        newApiClient().invokeApi(
            {},
            '/resolve_station',
            'GET',
            {
                queryParams: { site: station.site, stationNumber: station.stationNumber },
                headers: { Authorization: drishtiJwt() },
            },
            null,
        ),
        setResult,
    );
};

export const startDrishtiInstall = (
    request: StartDrishtiInstallRequest,
    setResult: (success: boolean, result: StartDrishtiInstallResponse | ErrorResponse) => void,
) => {
    dispatchResponse<StartDrishtiInstallResponse>(
        newApiClient().invokeApi(
            {},
            '/start_drishti_install',
            'POST',
            {
                headers: {
                    Authorization: drishtiJwt(),
                    'Content-Type': 'application/json',
                },
            },
            request,
        ),
        setResult,
    );
};

export const endDrishtiInstall = (
    request: EndDrishtiInstallRequest,
    setResult: (success: boolean, result: EndDrishtiInstallResponse | ErrorResponse) => void,
) => {
    dispatchResponse<EndDrishtiInstallResponse>(
        newApiClient().invokeApi(
            {},
            '/end_drishti_install',
            'POST',
            {
                headers: {
                    Authorization: drishtiJwt(),
                    'Content-Type': 'application/json',
                },
            },
            request,
        ),
        setResult,
    );
};

// The guide bucket is private, so this presigned link is the only way in and it expires within the hour.
export const getDrishtiInstallGuide = (
    setResult: (success: boolean, result: GetDrishtiInstallGuideResponse | ErrorResponse) => void,
) => {
    dispatchResponse<GetDrishtiInstallGuideResponse>(
        newApiClient().invokeApi(
            {},
            '/get_drishti_install_guide',
            'GET',
            { headers: { Authorization: drishtiJwt() } },
            null,
        ),
        setResult,
    );
};

// ---------------------------------------------------------------------------
// Calibration handlers
// ---------------------------------------------------------------------------

/**
 * Start a calibration run for a device.
 *
 * POST /drishti_start_calibration with the request body and the tech's JWT. Backend kicks off
 * the calibration workflow and writes the corresponding Leo state transition.
 */
export const startCalibration = (
    request: StartCalibrationRequest,
    setResult: (success: boolean, result: StartCalibrationResponse | ErrorResponse) => void,
) => {
    dispatchResponse<StartCalibrationResponse>(
        newApiClient().invokeApi(
            {},
            '/drishti_start_calibration',
            'POST',
            {
                headers: {
                    Authorization: drishtiJwt(),
                    'Content-Type': 'application/json',
                },
            },
            request,
        ),
        setResult,
    );
};

/**
 * Poll for the current calibration status of a device.
 *
 * GET /drishti_calibration_status?deviceId=... — passthrough to the calibration service's
 * query, resolves any `calibrationResultS3Uri` and inlines the result JSON.
 */
export const pollCalibrationStatus = (
    deviceId: string,
    setResult: (success: boolean, result: CalibrationStatusResponse | ErrorResponse) => void,
) => {
    dispatchResponse<CalibrationStatusResponse>(
        newApiClient().invokeApi(
            {},
            '/drishti_calibration_status',
            'GET',
            {
                queryParams: { deviceId },
                headers: { Authorization: drishtiJwt() },
            },
            null,
        ),
        setResult,
    );
};

/**
 * Record the terminal calibration outcome (APPLIED or FAILED) in Leo and get back the
 * escalation decision. Called by the calibration page when polling detects a terminal status.
 *
 * POST /drishti_calibration_complete with {deviceId, outcome}. The backend records
 * the outcome in Leo and returns {escalated, failedAttemptCount}.
 */
export const completeCalibration = (
    deviceId: string,
    outcome: 'APPLIED' | 'FAILED',
    dryRun: boolean,
    setResult: (success: boolean, result: CompleteCalibrationResponse | ErrorResponse) => void,
) => {
    dispatchResponse<CompleteCalibrationResponse>(
        newApiClient().invokeApi(
            {},
            '/drishti_calibration_complete',
            'POST',
            {
                headers: {
                    Authorization: drishtiJwt(),
                    'Content-Type': 'application/json',
                },
            },
            { deviceId, outcome, dryRun },
        ),
        setResult,
    );
};
